import { Link, useParams, Navigate } from "react-router-dom";
import { MapPin, Check, ArrowRight, Phone } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaSection } from "@/components/CtaSection";
import { Reveal } from "@/components/Reveal";
import { REGIONS, findRegion } from "@/lib/areas";
import { SERVICES, isPlaceholder } from "@/lib/content";
import { useSite } from "@/lib/SiteContext";
import { useSeo } from "@/hooks/useSeo";

export default function AreaRegion() {
  const { slug } = useParams();
  const region = findRegion(slug);
  const { settings } = useSite();

  useSeo(
    region ? `${region.title} | RD Cleaning Services` : "Service Area | RD Cleaning Services",
    region
      ? `${region.blurb} Call RD Cleaning Services for a free quote.`.slice(0, 300)
      : "Cleaning services across Perth and regional Western Australia."
  );

  if (!region) return <Navigate to="/areas" replace />;

  const others = REGIONS.filter((r) => r.slug !== region.slug);
  const phone = settings.phone;

  return (
    <>
      <PageHero
        eyebrow={`Service Area · ${region.short}`}
        title={region.title}
        subtitle={region.blurb}
        image={region.image}
      >
        <Link to="/quote" className="focus-ring rounded-full bg-teal-500 px-7 py-3.5 text-sm font-bold text-navy-deep transition-all hover:-translate-y-0.5 hover:bg-teal-400" data-testid="area-quote-button">
          Get a Free Quote
        </Link>
        {!isPlaceholder(phone) && (
          <a href={`tel:${phone.replace(/\s/g, "")}`} className="focus-ring inline-flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:border-teal-300 hover:text-teal-300" data-testid="area-call-button">
            <Phone className="h-4 w-4" /> {phone}
          </a>
        )}
      </PageHero>

      {/* Suburbs */}
      <section className="bg-white py-20 sm:py-24" data-testid="area-suburbs-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">Suburbs &amp; Towns</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Areas We Cover in {region.short}
            </h2>
            <p className="mt-4 text-slate-600">
              These are the main localities we service in this area. If your suburb isn't listed, ask us
              anyway — we're flexible and happy to confirm.
            </p>
          </Reveal>
          <ul className="mt-10 flex flex-wrap gap-2.5" data-testid="area-suburb-list">
            {region.suburbs.map((s) => (
              <li
                key={s}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-medium text-navy"
                data-testid={`area-suburb-${s.toLowerCase().replace(/\s/g, "-")}`}
              >
                <MapPin className="h-3.5 w-3.5 text-teal-600" /> {s}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* What we do here */}
      <section className="bg-slate-50 py-20 sm:py-24" data-testid="area-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <Reveal>
              <p className="eyebrow">What We Do Here</p>
              <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                Cleaning That Suits {region.short}
              </h2>
              <ul className="mt-8 space-y-4">
                {region.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-slate-700">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-600">
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className="text-sm leading-relaxed">{h}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid gap-4 sm:grid-cols-2">
                {SERVICES.slice(0, 4).map((s) => (
                  <Link
                    key={s.id}
                    to={s.link}
                    className="focus-ring group rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-1 hover:border-teal-300 hover:shadow-md"
                    data-testid={`area-service-link-${s.id}`}
                  >
                    <h3 className="font-display text-sm font-bold text-navy">{s.name}</h3>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{s.desc}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-teal-700">
                      Learn more <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other areas */}
      <section className="bg-white py-16" data-testid="area-other-regions">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-xl font-bold text-navy">Other Areas We Service</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {others.map((r) => (
              <Link
                key={r.slug}
                to={`/areas/${r.slug}`}
                className="focus-ring inline-flex items-center gap-2 rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-teal-300 hover:text-teal-700"
                data-testid={`area-other-${r.slug}`}
              >
                {r.name} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaSection
        heading={`Need a Cleaner in ${region.short}?`}
        text="Tell us your suburb and what you need cleaned — we'll come back with a free, no-obligation quote."
      />
    </>
  );
}
